/**
 * Bagveda Rewards Page — custom Figma UI + Nector APIs
 *
 * UI is Bagveda only. Nector is the rewards engine (wallet, triggers, offers, referrals).
 * Do not load data-op="reward" — that injects Nector's dashboard.
 * Writes use the storefront key + x-leadtoken from the existing floating widget.
 */

(function () {
  const ICONS = {
    review: 'review',
    order: 'order',
    purchase: 'order',
    birthday: 'birthday',
    signup: 'account',
    account: 'account',
    register: 'account',
    referral: 'refer',
    refer: 'refer',
    instagram: 'instagram',
    social: 'instagram',
  };

  function readConfig() {
    const node = document.getElementById('bagveda-nector-config');
    if (!node) return null;

    try {
      const config = JSON.parse(node.textContent);
      config.apiKey = config.apiKey || resolveApiKey();
      return config;
    } catch (error) {
      console.warn('[Bagveda Rewards] Invalid Nector config', error);
      return null;
    }
  }

  function resolveApiKey() {
    if (window.bagvedaNector && typeof window.bagvedaNector.resolveApiKey === 'function') {
      return window.bagvedaNector.resolveApiKey() || '';
    }

    return (
      window.nector?.api_key ||
      window.nector_data?._auth?.api_key ||
      document.querySelector('script[src*="nector"][data-api_key]')?.getAttribute('data-api_key') ||
      document.querySelector('script[data-api_key]')?.getAttribute('data-api_key') ||
      ''
    );
  }

  function getItems(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.data?.items)) return payload.data.items;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
  }

  function getObject(payload) {
    if (!payload) return null;
    if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
      return payload.data;
    }
    return payload;
  }

  function getText(value, fallback = '') {
    if (value == null || value === '') return fallback;
    return String(value);
  }

  function compactRewardValue(value) {
    const text = String(value == null ? '' : value).trim();
    if (!text) return '';

    const percent = text.match(/(\d+(?:\.\d+)?)\s*%/);
    if (percent) return `${percent[1]}%`;

    const amount = text.match(/^(\d{1,5})(?:\s|$)/);
    if (amount) return amount[1];

    return text.length > 8 ? text.slice(0, 8) : text;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function randomUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = (Math.random() * 16) | 0;
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function waitFor(predicate, timeoutMs = 12000, intervalMs = 200) {
    return new Promise((resolve) => {
      const existing = predicate();
      if (existing) {
        resolve(existing);
        return;
      }

      const started = Date.now();
      const timer = window.setInterval(() => {
        const value = predicate();
        if (value || Date.now() - started >= timeoutMs) {
          window.clearInterval(timer);
          resolve(value || null);
        }
      }, intervalMs);
    });
  }

  function readLeadAuth() {
    const nector = window.nector || {};
    const data = window.nector_data || {};
    const auth = data._auth || {};

    return {
      token:
        nector.lead_token ||
        nector.leadToken ||
        auth.lead_token ||
        auth.leadToken ||
        data.lead_token ||
        null,
      digest: nector.lead_digest || auth.lead_digest || null,
      timestamp: nector.timestamp || auth.timestamp || null,
      customerId: auth.customer_id || nector.business_customer_id || nector.customer_id || null,
      apiKey: nector.api_key || auth.api_key || resolveApiKey(),
      lead: nector.lead || data.lead || data.item || null,
    };
  }

  function readLeadToken() {
    return readLeadAuth().token;
  }

  function waitForLeadToken(timeoutMs = 12000) {
    return waitFor(() => readLeadToken(), timeoutMs);
  }

  function waitForLeadAuth(timeoutMs = 12000) {
    return waitFor(() => {
      const auth = readLeadAuth();
      if (auth.token || (auth.digest && auth.timestamp) || auth.lead) return auth;
      return null;
    }, timeoutMs);
  }

  function unwrapLead(payload) {
    const data = getObject(payload) || payload;
    if (!data || typeof data !== 'object') return null;

    if (data.item && typeof data.item === 'object' && !Array.isArray(data.item)) {
      return data.item;
    }

    if (data.lead && typeof data.lead === 'object' && !Array.isArray(data.lead)) {
      return data.lead;
    }

    return data;
  }

  function inferIcon(trigger) {
    const haystack = [
      trigger.slug,
      trigger.handle,
      trigger.type,
      trigger.trigger_type,
      trigger.name,
      trigger.title,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    for (const [needle, icon] of Object.entries(ICONS)) {
      if (haystack.includes(needle)) return icon;
    }

    return 'account';
  }

  function cardTypeFromEl(card) {
    if (card.dataset.triggerType) return card.dataset.triggerType;
    const src = card.querySelector('.bagveda-rewards-page__earn-icon img')?.getAttribute('src') || '';
    const match = src.match(/bagveda-rewards-icon-([a-z]+)/i);
    return match ? match[1] : '';
  }

  function formatRewardBadge(trigger, coinName) {
    const rewardText =
      trigger.reward_text ||
      trigger.reward_label ||
      trigger.badge_text ||
      trigger.short_description;

    if (rewardText) return rewardText;

    const amount =
      trigger.reward_amount ??
      trigger.coin_amount ??
      trigger.points ??
      trigger.amount ??
      trigger.value;

    if (amount == null || amount === '') return 'Earn coins';

    const numeric = Number(amount);
    if (!Number.isNaN(numeric) && numeric > 0 && numeric <= 10) {
      return `Earn on every ₹${numeric * 100 || 100}`;
    }

    if (!Number.isNaN(numeric) && numeric > 0) {
      return `+${numeric} ${coinName}`;
    }

    return String(amount);
  }

  function getWalletAvailable(lead) {
    if (!lead) return 0;
    const source = lead.item && typeof lead.item === 'object' ? lead.item : lead;
    const wallet = source.wallet || source.walletdetail || source.wallet_detail || {};
    const value =
      wallet.available ??
      wallet.balance ??
      wallet.coins ??
      source.available ??
      source.coins ??
      0;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  function getReferralCode(lead) {
    if (!lead) return '';
    const source = lead.item && typeof lead.item === 'object' ? lead.item : lead;
    return getText(
      source.referral_code ||
        source.referralcode ||
        source.referral?.code ||
        source.referral?.referral_code
    );
  }

  function getReferralShareLink(lead, origin) {
    if (!lead) return '';
    const source = lead.item && typeof lead.item === 'object' ? lead.item : lead;
    const direct = source.referral_link || source.referral_url || source.referral?.link || source.referral?.url;
    if (direct) return String(direct);
    const code = getReferralCode(lead);
    if (!code) return '';
    return `${origin}/?nector_referral_code=${encodeURIComponent(code)}`;
  }

  function getErrorMessage(payload, fallback) {
    return (
      payload?.meta?.message ||
      payload?.message ||
      payload?.error ||
      payload?.data?.message ||
      fallback
    );
  }

  function isAuthFailure(response, payload) {
    if (response.status === 401 || response.status === 403) return true;
    const code = payload && payload.meta && payload.meta.code;
    return code === 401 || code === 403;
  }

  function withReturnTo(url) {
    if (!url) return `/account/login?return_to=${encodeURIComponent(window.location.pathname)}`;
    try {
      const parsed = new URL(url, window.location.origin);
      if (!parsed.searchParams.has('return_to')) {
        parsed.searchParams.set('return_to', window.location.pathname);
      }
      return `${parsed.pathname}${parsed.search}`;
    } catch (error) {
      return url;
    }
  }

  function parseOffers(payload) {
    const items = getItems(payload);
    const looksLikeOffer = (item) =>
      item && (item.offer_id || item._id || item.id) && (item.coin_amount != null || item.title || item.name);

    if (items.length && looksLikeOffer(items[0])) return items;

    const obj = getObject(payload) || {};
    const nested = [];
    ['offers', 'waystoredeem', 'items', 'data'].forEach((key) => {
      if (Array.isArray(obj[key])) nested.push(...obj[key]);
    });

    Object.keys(obj).forEach((key) => {
      if (Array.isArray(obj[key])) nested.push(...obj[key]);
    });

    const unique = [];
    const seen = new Set();
    nested.filter(looksLikeOffer).forEach((item) => {
      const id = item._id || item.id || item.offer_id;
      if (seen.has(id)) return;
      seen.add(id);
      unique.push(item);
    });

    return unique.length ? unique : items.filter(looksLikeOffer);
  }

  class BagvedaRewardsPage {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.triggers = [];
      this.offers = [];
      this.lead = null;
      this.leadToken = null;
      this.leadDigest = null;
      this.leadTimestamp = null;
      this.activeModal = null;
    }

    applyLeadAuth(auth) {
      if (!auth) return;

      if (auth.token) this.leadToken = auth.token;
      if (auth.digest) this.leadDigest = auth.digest;
      if (auth.timestamp) this.leadTimestamp = auth.timestamp;
      if (auth.apiKey && !this.config.apiKey) this.config.apiKey = auth.apiKey;
      if (auth.customerId && !this.config.customerId) this.config.customerId = auth.customerId;
      if (auth.lead) this.lead = unwrapLead(auth.lead) || auth.lead;
    }

    hydrateFromNectorClient() {
      const auth = readLeadAuth();
      this.applyLeadAuth(auth);
    }

    bindNectorEvents() {
      const refresh = () => {
        this.hydrateFromNectorClient();
        if (!this.config.apiKey) this.config.apiKey = resolveApiKey();
        this.renderWallet();
        this.syncReferralLink();
      };

      window.addEventListener('nector_lead_updated', refresh);
      window.addEventListener('nector_widget_initialized', refresh);
    }

    async init() {
      this.seedCardTypes();
      this.bindActions();
      this.bindNectorEvents();
      this.hydrateFromNectorClient();
      this.renderWallet();
      this.syncReferralLink();

      if (!this.config.apiKey) {
        this.config.apiKey = (await waitFor(() => resolveApiKey() || readLeadAuth().apiKey, 8000)) || '';
      }

      if (!this.config.apiKey) return;

      await Promise.all([
        this.loadEarnTriggers(),
        this.loadProgramDetails(),
        this.loadRedeemOffers(),
      ]);

      if (this.config.customerId || readLeadAuth().customerId) {
        this.applyLeadAuth((await waitForLeadAuth()) || readLeadAuth());
        await this.loadLead();
      }
    }

    seedCardTypes() {
      this.root.querySelectorAll('[data-bagveda-earn-card]').forEach((card) => {
        if (!card.dataset.triggerType) {
          card.dataset.triggerType = cardTypeFromEl(card);
        }
      });
    }

    cachefrontUrl() {
      return (this.config.cachefrontUrl || 'https://cachefront.nector.io/api/v2/merchant').replace(/\/$/, '');
    }

    platformUrl() {
      const base = (this.config.platformUrl || 'https://platform.nector.io').replace(/\/$/, '');
      return `${base}/api/v2/merchant`;
    }

    merchantBases() {
      const cachefront = this.cachefrontUrl();
      const platform = this.platformUrl();
      return cachefront === platform ? [cachefront] : [cachefront, platform];
    }

    buildGetHeaders() {
      const headers = {
        Accept: 'application/json',
        'x-apikey': this.config.apiKey,
        'x-source': 'web',
      };

      if (this.config.workspaceId) {
        headers['x-workspaceid'] = this.config.workspaceId;
      }

      if (this.leadToken) {
        headers['x-leadtoken'] = this.leadToken;
      } else if (this.leadDigest && this.leadTimestamp) {
        headers['x-leaddigest'] = this.leadDigest;
        headers['x-timestamp'] = String(this.leadTimestamp);
      }

      return headers;
    }

    buildWriteHeaders() {
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-apikey': this.config.apiKey,
        'x-source': 'web',
      };

      if (this.config.workspaceId) {
        headers['x-workspaceid'] = this.config.workspaceId;
      }

      if (this.leadToken) {
        headers['x-leadtoken'] = this.leadToken;
      }

      return headers;
    }

    async nectorGet(path, { auth = false, query = '' } = {}) {
      const suffix = query ? `${path}?${query}` : path;
      let lastError = new Error('Nector request failed');

      const requestOnce = async () => {
        for (const base of this.merchantBases()) {
          let response;
          try {
            response = await fetch(`${base}${suffix}`, {
              headers: this.buildGetHeaders(),
            });
          } catch (error) {
            lastError = error;
            continue;
          }

          let payload = null;
          try {
            payload = await response.json();
          } catch (error) {
            lastError = error;
            continue;
          }

          if (isAuthFailure(response, payload)) {
            const error = new Error(getErrorMessage(payload, 'Unauthorized'));
            error.code = 'auth';
            error.payload = payload;
            throw error;
          }

          if (!response.ok) {
            lastError = new Error(getErrorMessage(payload, `Nector request failed: ${response.status}`));
            continue;
          }

          return payload;
        }

        throw lastError;
      };

      try {
        return await requestOnce();
      } catch (error) {
        if (error.code === 'auth' && auth) {
          this.applyLeadAuth((await waitForLeadAuth(4000)) || readLeadAuth());
          return requestOnce();
        }

        throw error;
      }
    }

    async nectorWrite(path, body, method = 'POST') {
      if (!this.leadToken) {
        this.leadToken = (await waitForLeadToken(4000)) || this.leadToken;
      }

      const response = await fetch(`${this.platformUrl()}${path}`, {
        method,
        headers: this.buildWriteHeaders(),
        body: JSON.stringify(body),
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }

      if (!response.ok) {
        const error = new Error(getErrorMessage(payload, `Nector request failed: ${response.status}`));
        error.payload = payload;
        error.code = isAuthFailure(response, payload) ? 'auth' : 'write';
        throw error;
      }

      return payload;
    }

    async loadEarnTriggers() {
      try {
        const payload = await this.nectorGet('/triggers', {
          query: 'limit=100&sort=updated_at&sort_op=DESC',
        });
        this.triggers = getItems(payload).filter((trigger) => trigger && trigger.status !== 'inactive' && trigger.enabled !== false);
        this.renderEarnCards();
      } catch (error) {
        console.warn('[Bagveda Rewards] Unable to load earn triggers', error);
      }
    }

    renderEarnCards() {
      if (!this.triggers.length) return;

      const cards = Array.from(this.root.querySelectorAll('[data-bagveda-earn-card]'));
      const used = new Set();

      this.triggers.forEach((trigger) => {
        const icon = inferIcon(trigger);
        const card =
          cards.find((item) => !used.has(item) && cardTypeFromEl(item) === icon) ||
          cards.find((item) => !used.has(item) && !item.dataset.triggerId);

        if (!card) return;
        used.add(card);

        const title = getText(
          trigger.title || trigger.name || trigger.display_name,
          card.querySelector('[data-bagveda-earn-title]')?.textContent
        );
        const copy = getText(
          trigger.description || trigger.subtitle || trigger.help_text,
          card.querySelector('[data-bagveda-earn-copy]')?.textContent
        );
        const badge = formatRewardBadge(trigger, this.config.coinName || 'Bagveda Coins');
        const titleEl = card.querySelector('[data-bagveda-earn-title]');
        const copyEl = card.querySelector('[data-bagveda-earn-copy]');
        const badgeEl = card.querySelector('[data-bagveda-earn-badge]');
        const iconEl = card.querySelector('.bagveda-rewards-page__earn-icon img');

        if (titleEl) titleEl.textContent = title;
        if (copyEl && copy) copyEl.textContent = copy;
        if (badgeEl) badgeEl.textContent = badge;
        if (iconEl && this.config.assetRoot) {
          iconEl.src = `${this.config.assetRoot}bagveda-rewards-icon-${icon}.svg`;
        }

        card.dataset.triggerId = getText(trigger._id || trigger.id || trigger.trigger_id);
        card.dataset.triggerTitle = title;
        card.dataset.triggerType = getText(trigger.slug || trigger.type || trigger.trigger_type || icon);
      });
    }

    async loadProgramDetails() {
      try {
        const payload = await this.nectorGet('/aggreegateddetails');
        const details = getObject(payload);
        if (!details) return;

        const referral =
          details.referral ||
          details.referralprogram ||
          details.referral_program ||
          details.referralsettings ||
          {};

        const friendValueEl = this.root.querySelector('[data-bagveda-referral-friend-value]');
        const youValueEl = this.root.querySelector('[data-bagveda-referral-you-value]');
        const youSubtitleEl = this.root.querySelector('[data-bagveda-referral-you-subtitle]');

        const friendValue =
          referral.referred_reward_text ||
          referral.referee_reward_text ||
          (referral.referred_discount_percentage != null
            ? `${referral.referred_discount_percentage}%`
            : null) ||
          (referral.referee_discount_percentage != null
            ? `${referral.referee_discount_percentage}%`
            : null);

        const youValue =
          referral.referrer_reward_amount ??
          referral.referrer_reward_text ??
          referral.referrer_coins ??
          null;

        if (friendValueEl && friendValue) friendValueEl.textContent = compactRewardValue(friendValue);
        if (youValueEl && youValue != null) youValueEl.textContent = compactRewardValue(youValue);
        if (youSubtitleEl) {
          youSubtitleEl.textContent = getText(
            referral.reward_coin_name || referral.coin_name,
            youSubtitleEl.textContent
          );
        }

        const wallet = details.wallet || details.walletsettings || {};
        const conversionText =
          wallet.conversion_text ||
          wallet.redemption_text ||
          (wallet.coin_amount && wallet.discount_amount
            ? `Every ${wallet.coin_amount} ${this.config.coinName || 'Bagveda Coins'} = ₹${wallet.discount_amount}`
            : null);

        if (conversionText) {
          const rateEl = this.root.querySelector('[data-bagveda-redeem-title]');
          if (rateEl) rateEl.textContent = conversionText;
        }
      } catch (error) {
        console.warn('[Bagveda Rewards] Unable to load aggregated program details', error);
      }
    }

    async loadRedeemOffers() {
      try {
        const payload = await this.nectorGet('/aggreegatedoffers');
        this.offers = parseOffers(payload);
        const offer = this.offers[0];
        if (!offer) return;

        const coinName = this.config.coinName || 'Bagveda Coins';
        const rate =
          offer.title ||
          offer.name ||
          offer.display_name ||
          (offer.coin_amount && offer.discount_amount
            ? `Every ${offer.coin_amount} ${coinName} = ₹${offer.discount_amount}`
            : null);

        const note = offer.description || offer.subtitle || offer.help_text;
        const minimum =
          offer.minimum_coins != null
            ? `Starts from ${offer.minimum_coins} ${coinName}`
            : offer.minimum_text || offer.min_coins_text;

        if (rate) {
          const rateEl = this.root.querySelector('[data-bagveda-redeem-title]');
          if (rateEl) rateEl.textContent = rate;
        }

        if (note) {
          const noteEl = this.root.querySelector('[data-bagveda-redeem-note]');
          if (noteEl) noteEl.textContent = note;
        }

        if (minimum) {
          const minEl = this.root.querySelector('[data-bagveda-redeem-minimum]');
          if (minEl) minEl.textContent = minimum;
        }
      } catch (error) {
        console.warn('[Bagveda Rewards] Unable to load aggregated offers', error);
      }
    }

    async loadLead() {
      this.hydrateFromNectorClient();
      this.renderWallet();
      this.syncReferralLink();

      const customerIds = [];
      const auth = readLeadAuth();
      [auth.customerId, this.config.customerId, this.config.customerId ? `shopify-${this.config.customerId}` : '']
        .map((value) => (value == null || value === '' ? '' : String(value)))
        .filter(Boolean)
        .forEach((value) => {
          if (!customerIds.includes(value)) customerIds.push(value);
        });

      if (!customerIds.length) return;

      let lastError = null;

      for (const customerId of customerIds) {
        try {
          const payload = await this.nectorGet(`/leads/${randomUuid()}`, {
            auth: true,
            query: `customer_id=${encodeURIComponent(customerId)}`,
          });
          const data = getObject(payload) || payload;
          const token = data?.lead_token || payload?.lead_token;
          if (token) this.leadToken = token;
          this.lead = unwrapLead(payload) || this.lead;
          this.renderWallet();
          this.syncReferralLink();
          return;
        } catch (error) {
          lastError = error;
        }
      }

      if (lastError) {
        console.warn('[Bagveda Rewards] Unable to load wallet', lastError);
        this.hydrateFromNectorClient();
        this.renderWallet();
        this.syncReferralLink();
      }
    }

    renderWallet() {
      const balanceEl = this.root.querySelector('[data-bagveda-wallet-balance]');
      if (!balanceEl || !this.lead) return;

      const available = getWalletAvailable(this.lead);
      const coinName = this.config.coinName || 'Bagveda Coins';
      const formatted = new Intl.NumberFormat('en-IN').format(available);
      balanceEl.textContent = `${formatted} ${coinName}`;
    }

    referralLink() {
      return getReferralShareLink(this.lead, window.location.origin);
    }

    syncReferralLink() {
      const link = this.referralLink();
      this.root.querySelectorAll('[data-bagveda-referral-link]').forEach((input) => {
        input.value = link;
      });
      const whatsapp = this.root.querySelector('[data-bagveda-whatsapp-referral]');
      if (whatsapp) {
        const message = link || window.location.href;
        whatsapp.href = `https://wa.me/?text=${encodeURIComponent(message)}`;
      }
    }

    scrollToSection(target) {
      if (!target) return;

      const headerOffset = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    bindActions() {
      this.root.addEventListener('click', (event) => {
        const scrollLink = event.target.closest('[data-bagveda-scroll]');
        if (scrollLink) {
          const selector = scrollLink.getAttribute('data-bagveda-scroll') || scrollLink.getAttribute('href');
          const target = selector ? this.root.querySelector(selector) : null;
          if (target) {
            event.preventDefault();
            this.scrollToSection(target);
          }
          return;
        }

        const closer = event.target.closest('[data-bagveda-rewards-close]');
        if (closer) {
          event.preventDefault();
          this.closeModals();
          return;
        }

        const copyBtn = event.target.closest('[data-bagveda-copy-referral]');
        if (copyBtn) {
          event.preventDefault();
          this.copyReferralLink(copyBtn);
          return;
        }

        const birthdaySubmit = event.target.closest('[data-bagveda-birthday-submit]');
        if (birthdaySubmit) {
          event.preventDefault();
          this.submitBirthday(birthdaySubmit);
          return;
        }

        const redeemOffer = event.target.closest('[data-bagveda-redeem-offer]');
        if (redeemOffer) {
          event.preventDefault();
          this.redeemOffer(redeemOffer);
          return;
        }

        const earnCard = event.target.closest('[data-bagveda-earn-card]');
        if (earnCard) {
          event.preventDefault();
          this.handleEarnAction(earnCard);
          return;
        }

        const actionTarget = event.target.closest('[data-bagveda-rewards-action]');
        if (actionTarget) {
          event.preventDefault();
          this.handlePageAction(actionTarget.dataset.bagvedaRewardsAction);
        }
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.activeModal) {
          this.closeModals();
          return;
        }

        if (event.key !== 'Enter' && event.key !== ' ') return;
        const earnCard = event.target.closest('[data-bagveda-earn-card]');
        if (!earnCard) return;
        event.preventDefault();
        this.handleEarnAction(earnCard);
      });

      this.observeEarnCards();
    }

    observeEarnCards() {
      const cards = Array.from(this.root.querySelectorAll('[data-bagveda-earn-card]'));
      if (!cards.length) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        cards.forEach((card) => card.classList.add('is-visible'));
        return;
      }

      if (!('IntersectionObserver' in window)) {
        cards.forEach((card) => card.classList.add('is-visible'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          });
        },
        { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
      );

      cards.forEach((card) => observer.observe(card));
    }

    loginUrl() {
      return withReturnTo(this.config.loginUrl);
    }

    requireLogin() {
      if (this.config.customerId) return false;
      window.location.href = this.loginUrl();
      return true;
    }

    announce(message) {
      const live = this.root.querySelector('[data-bagveda-live]');
      if (!live) return;
      live.textContent = message;
      window.clearTimeout(this.liveTimer);
      this.liveTimer = window.setTimeout(() => {
        live.textContent = '';
      }, 4000);
    }

    setModalStatus(selector, message, isError = false) {
      const status = this.root.querySelector(selector);
      if (!status) return;
      status.textContent = message || '';
      status.classList.toggle('is-error', Boolean(isError && message));
    }

    openModal(name) {
      this.closeModals();
      const modal = this.root.querySelector(`[data-bagveda-rewards-modal="${name}"]`);
      if (!modal) return;
      modal.hidden = false;
      this.activeModal = modal;
      document.body.style.overflow = 'hidden';
      const panel = modal.querySelector('.bagveda-rewards-modal__panel');
      if (panel) panel.focus();
    }

    closeModals() {
      this.root.querySelectorAll('[data-bagveda-rewards-modal]').forEach((modal) => {
        modal.hidden = true;
      });
      this.activeModal = null;
      document.body.style.overflow = '';
    }

    handlePageAction(action) {
      if (action === 'referral') {
        if (this.requireLogin()) return;
        this.openReferralModal();
        return;
      }

      if (action === 'redeem') {
        if (this.requireLogin()) return;
        this.openRedeemModal();
      }
    }

    async openReferralModal() {
      if (!this.lead) await this.loadLead();
      this.syncReferralLink();
      this.openModal('referral');
      if (!this.referralLink()) {
        this.announce('Your referral link is not ready yet. Try again in a moment.');
      }
    }

    async openRedeemModal() {
      if (!this.offers.length) await this.loadRedeemOffers();
      if (!this.lead) await this.loadLead();
      this.renderRedeemOffers();
      this.setModalStatus('[data-bagveda-redeem-status]', '');
      this.openModal('redeem');
    }

    renderRedeemOffers() {
      const list = this.root.querySelector('[data-bagveda-redeem-offers]');
      if (!list) return;

      if (!this.offers.length) {
        list.innerHTML = '<p class="bagveda-rewards-modal__copy">No rewards are available to redeem right now.</p>';
        return;
      }

      const coinName = this.config.coinName || 'Bagveda Coins';
      const available = getWalletAvailable(this.lead);
      const redeemLabel = 'Redeem';

      list.innerHTML = this.offers
        .map((offer) => {
          const id = offer._id || offer.id || offer.offer_id;
          const title = offer.title || offer.name || offer.display_name || `${offer.coin_amount || ''} ${coinName}`.trim();
          const coins = offer.coin_amount ?? offer.coins ?? offer.amount;
          const discount = offer.discount_amount ?? offer.discount;
          const meta = [
            coins != null ? `${coins} ${coinName}` : '',
            discount != null ? `₹${discount} off` : '',
          ]
            .filter(Boolean)
            .join(' · ');
          const disabled = coins != null && available < Number(coins);

          return `
            <article class="bagveda-rewards-offer">
              <div>
                <p class="bagveda-rewards-offer__title">${escapeHtml(title)}</p>
                ${meta ? `<p class="bagveda-rewards-offer__meta">${escapeHtml(meta)}</p>` : ''}
              </div>
              <button
                type="button"
                class="bagveda-rewards-page__btn bagveda-rewards-page__btn--primary"
                data-bagveda-redeem-offer
                data-offer-id="${escapeHtml(id)}"
                ${disabled ? 'disabled' : ''}
              >
                ${escapeHtml(redeemLabel)}
              </button>
            </article>
          `;
        })
        .join('');
    }

    async redeemOffer(button) {
      const offerId = button.dataset.offerId;
      if (!offerId || !this.config.customerId) return;

      button.disabled = true;
      this.setModalStatus('[data-bagveda-redeem-status]', 'Redeeming…');

      try {
        const payload = await this.nectorWrite('/offerredeems', {
          offer_id: offerId,
          customer_id: String(this.config.customerId),
        });
        const coupon =
          payload?.data?.coupon ||
          payload?.data?.coupons?.[0] ||
          payload?.coupon ||
          getObject(payload);
        const code = coupon?.code || coupon?.coupon_code || coupon?.name;
        await this.loadLead();
        this.renderRedeemOffers();
        this.setModalStatus(
          '[data-bagveda-redeem-status]',
          code ? `Redeemed. Your code is ${code}` : 'Reward redeemed. Check your account for the coupon.'
        );
        if (this.lead?._id) await this.loadCoupons();
        this.announce(code ? `Redeemed. Coupon ${code}` : 'Reward redeemed.');
      } catch (error) {
        this.setModalStatus(
          '[data-bagveda-redeem-status]',
          error.message || 'Unable to redeem this reward right now.',
          true
        );
        button.disabled = false;
      }
    }

    async loadCoupons() {
      const list = this.root.querySelector('[data-bagveda-redeem-coupons]');
      if (!list || !this.lead?._id) return;

      try {
        const payload = await this.nectorGet('/coupons', {
          auth: true,
          query: `lead_id=${encodeURIComponent(this.lead._id)}&limit=20&sort=created_at&sort_op=DESC`,
        });
        const coupons = getItems(payload);
        if (!coupons.length) return;

        list.hidden = false;
        list.innerHTML = `<h3 class="bagveda-rewards-modal__subtitle">Your coupons</h3>${coupons
          .map((coupon) => {
            const code = coupon.code || coupon.coupon_code || coupon.name || '';
            const title = coupon.title || coupon.offer_title || 'Coupon';
            return `<article class="bagveda-rewards-offer">
              <div>
                <p class="bagveda-rewards-offer__title">${escapeHtml(title)}</p>
                <p class="bagveda-rewards-offer__meta">${escapeHtml(code)}</p>
              </div>
            </article>`;
          })
          .join('')}`;
      } catch (error) {
        console.warn('[Bagveda Rewards] Unable to load coupons', error);
      }
    }

    async copyReferralLink(button) {
      const row = button.closest('.bagveda-rewards-page__referral-inline-row, .bagveda-rewards-modal__row');
      const input = row?.querySelector('[data-bagveda-referral-link]') || this.root.querySelector('[data-bagveda-referral-link]');
      const link = input?.value || this.referralLink();
      if (!link) return;

      const original = button.textContent;
      const copied = this.root.getAttribute('data-copied-label') || 'Copied';

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else if (input) {
          input.select();
          document.execCommand('copy');
        }
        button.textContent = copied;
        this.announce(copied);
      } catch (error) {
        if (input) {
          input.select();
          document.execCommand('copy');
          button.textContent = copied;
        }
      }

      window.setTimeout(() => {
        button.textContent = original;
      }, 2000);
    }

    handleEarnAction(card) {
      const triggerType = (card?.dataset.triggerType || cardTypeFromEl(card) || '').toLowerCase();

      if (triggerType.includes('order') || triggerType.includes('purchase')) {
        window.location.href = this.config.catalogUrl || '/collections/all';
        return;
      }

      if (triggerType.includes('account') || triggerType.includes('signup') || triggerType.includes('register')) {
        if (!this.config.customerId) {
          window.location.href = this.config.registerUrl || '/account/register';
          return;
        }
        this.completeTrigger(card, 'Welcome bonus claimed.');
        return;
      }

      if (this.requireLogin()) return;

      if (triggerType.includes('review')) {
        window.location.href = this.config.accountUrl || '/account';
        return;
      }

      if (triggerType.includes('instagram') || triggerType.includes('social')) {
        const instagramUrl = this.root.getAttribute('data-instagram-url') || 'https://www.instagram.com/bagveda';
        window.open(instagramUrl, '_blank', 'noopener');
        this.completeTrigger(card, 'Instagram follow reward submitted.');
        return;
      }

      if (triggerType.includes('birthday')) {
        this.openBirthdayModal(card);
        return;
      }

      if (triggerType.includes('refer')) {
        this.openReferralModal();
        return;
      }

      this.completeTrigger(card);
    }

    openBirthdayModal(card) {
      this.pendingBirthdayCard = card;
      const input = this.root.querySelector('[data-bagveda-birthday-date]');
      const existing = this.lead?.metadetail?.dob || this.lead?.dob;
      if (input && existing) input.value = String(existing).slice(0, 10);
      this.setModalStatus('[data-bagveda-birthday-status]', '');
      this.openModal('birthday');
    }

    async submitBirthday(button) {
      const input = this.root.querySelector('[data-bagveda-birthday-date]');
      const date = input?.value;
      if (!date) {
        this.setModalStatus('[data-bagveda-birthday-status]', 'Add your birthday to continue.', true);
        return;
      }

      button.disabled = true;
      this.setModalStatus('[data-bagveda-birthday-status]', 'Saving…');

      try {
        const leadPath = this.lead?._id
          ? `/leads/${this.lead._id}`
          : `/leads/${randomUuid()}?customer_id=${encodeURIComponent(String(this.config.customerId))}`;

        try {
          await this.nectorWrite(leadPath, { metadetail: { dob: date } }, 'PUT');
        } catch (error) {
          console.warn('[Bagveda Rewards] Birthday update skipped', error);
        }

        await this.completeTrigger(this.pendingBirthdayCard, 'Birthday saved. Reward submitted.');
        this.setModalStatus('[data-bagveda-birthday-status]', 'Birthday saved.');
        await this.loadLead();
        window.setTimeout(() => this.closeModals(), 1200);
      } catch (error) {
        this.setModalStatus(
          '[data-bagveda-birthday-status]',
          error.message || 'Unable to save your birthday right now.',
          true
        );
      } finally {
        button.disabled = false;
      }
    }

    async completeTrigger(card, successMessage) {
      const triggerId = card?.dataset.triggerId;
      if (!triggerId) {
        this.announce('This reward is not available yet.');
        return;
      }

      if (!this.config.customerId) {
        window.location.href = this.loginUrl();
        return;
      }

      try {
        await this.nectorWrite('/activities', {
          trigger_id: triggerId,
          customer_id: String(this.config.customerId),
        });
        card.classList.add('is-complete');
        this.announce(successMessage || 'Reward submitted.');
        await this.loadLead();
      } catch (error) {
        this.announce(error.message || 'Unable to complete this reward right now.');
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const config = readConfig();
    const root = document.querySelector('.bagveda-rewards-page');

    if (!root || !config) return;

    const page = new BagvedaRewardsPage(root, config);
    page.init();
  });
})();
